<template>
  <div class="plan">
    <div v-if="loading" class="plan__state"><span class="mdi mdi-loading plan__spin"></span> Cargando tu plan…</div>
    <div v-else-if="loadError" class="plan__state plan__state--error">
      {{ loadError }} <button class="plan__retry" @click="load">Reintentar</button>
    </div>

    <template v-else-if="overview">
      <!-- Banner del periodo de prueba -->
      <div v-if="overview.trial" class="banner">
        <span class="mdi mdi-clock-outline banner__icon"></span>
        <span class="banner__text">
          <template v-if="overview.trial.expired">Tu periodo de prueba terminó el {{ formatLongDate(overview.trial.endsAt) }} · Activa tu suscripción para seguir usando Parkos</template>
          <template v-else>Estás en periodo de prueba · {{ overview.trial.daysLeft }} {{ overview.trial.daysLeft === 1 ? 'día restante' : 'días restantes' }} · Activa tu suscripción para continuar usando Parkos después del {{ formatLongDate(overview.trial.endsAt) }}</template>
        </span>
        <button class="banner__btn" @click="chooser?.scrollIntoView()">Activar suscripción</button>
      </div>

      <p v-if="readOnly" class="plan__ro">Estás viendo este parque como superadmin: los cambios están deshabilitados.</p>

      <PlanSummaryCard :overview="overview" />
      <PlanChargeCards :overview="overview" @card="modal = { type: 'card' }" />
      <PlanFiscalCard :park-id="parkId" :fiscal="overview.fiscal" :read-only="readOnly" @saved="onFiscalSaved" />
      <PlanInvoices :invoices="overview.invoices" :busy="downloading" @download="onDownload" @preview="(i) => (modal = { type: 'invoice', invoice: i })" />
      <PlanChooser ref="chooser" :overview="overview" :busy="saving" :read-only="readOnly" @select="(p) => { actionError = ''; modal = { type: 'change', plan: p } }" @cancel-request="onCancelRequest" />

      <p v-if="actionError" class="plan__error">{{ actionError }}</p>
    </template>

    <!-- Tarjeta (Etapa B) -->
    <PlanModal v-if="modal?.type === 'card'" title="Pago con tarjeta" @close="modal = null">
      <p>El pago con tarjeta estará disponible muy pronto. Por ahora Parkos coordina contigo el pago de tu suscripción (por ejemplo, por transferencia) y registra cada pago en tu cuenta.</p>
      <p>Cuando esté listo, podrás guardar tu tarjeta aquí mismo, de forma segura: los datos de la tarjeta nunca pasan por los servidores de Parkos.</p>
      <template #footer><button class="btn" @click="modal = null">Entendido</button></template>
    </PlanModal>

    <!-- Confirmar cambio de plan -->
    <PlanModal v-if="modal?.type === 'change'" :title="overview?.trial ? 'Solicitar este plan' : 'Cambiar de plan'" @close="!saving && (modal = null)">
      <ul class="sum">
        <li v-if="overview.plan">Cambiarás de <strong>Plan {{ overview.plan.name }}</strong> a <strong>Plan {{ modal.plan.name }}</strong></li>
        <li v-else>Contratarás el <strong>Plan {{ modal.plan.name }}</strong></li>
        <li v-if="overview.nextCharge?.date">El cambio aplicará el <strong>{{ formatLongDate(overview.nextCharge.date) }}</strong> (tu siguiente cobro)</li>
        <li v-else>Parkos se pondrá en contacto contigo para activarlo</li>
        <li>Tu nuevo monto mensual será <strong>${{ formatMoney(newAmount, true) }}</strong></li>
      </ul>
      <p v-if="actionError" class="plan__error">{{ actionError }}</p>
      <template #footer>
        <button class="btn btn--ghost" :disabled="saving" @click="modal = null">Cancelar</button>
        <button class="btn" :disabled="saving" @click="confirmChange">{{ saving ? 'Enviando…' : overview.trial ? 'Solicitar plan' : 'Confirmar cambio de plan' }}</button>
      </template>
    </PlanModal>

    <!-- Vista previa del comprobante -->
    <PlanModal v-if="modal?.type === 'invoice'" :title="`Comprobante ${modal.invoice.folio}`" @close="modal = null">
      <dl class="rcp">
        <div><dt>Fecha de pago</dt><dd>{{ formatLongDate(modal.invoice.issuedAt) }}</dd></div>
        <div><dt>Periodo</dt><dd>{{ modal.invoice.period }}</dd></div>
        <div><dt>Plan</dt><dd>{{ modal.invoice.planName }}</dd></div>
        <div><dt>Método</dt><dd>{{ METHOD_LABEL[modal.invoice.method] || 'No especificado' }}</dd></div>
        <div v-if="modal.invoice.reference"><dt>Referencia</dt><dd class="mono">{{ modal.invoice.reference }}</dd></div>
        <div class="rcp__total"><dt>Total pagado</dt><dd class="mono">${{ formatMoney(modal.invoice.amount, true) }} MXN</dd></div>
      </dl>
      <p class="rcp__note">Es un comprobante de pago, no una factura fiscal (CFDI).</p>
      <template #footer>
        <button class="btn btn--ghost" @click="modal = null">Cerrar</button>
        <button class="btn" :disabled="downloading" @click="onDownload(modal.invoice)">Descargar PDF</button>
      </template>
    </PlanModal>

    <div v-if="toast" class="toast" role="status">{{ toast }}</div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from '@/services/authStore'
import { getPlanOverview, requestPlanChange, downloadReceipt } from '@/services/planBilling'
import PlanSummaryCard from './Plan/PlanSummaryCard.vue'
import PlanChargeCards from './Plan/PlanChargeCards.vue'
import PlanFiscalCard from './Plan/PlanFiscalCard.vue'
import PlanInvoices from './Plan/PlanInvoices.vue'
import PlanChooser from './Plan/PlanChooser.vue'
import PlanModal from './Plan/PlanModal.vue'
import { formatLongDate, formatMoney, METHOD_LABEL, errorText } from './Plan/planFormat'

const props = defineProps({ parkId: { type: String, required: true } })

const { state: authState } = useAuthStore()
// Un superadmin que ve el parque como cliente puede mirar, pero no cambiar nada.
const readOnly = computed(() => !!authState.user?.impersonation)

const overview = ref(null)
const loading = ref(true)
const loadError = ref('')
const modal = ref(null)
const saving = ref(false)
const downloading = ref(false)
const actionError = ref('')
const toast = ref('')
const chooser = ref(null)

let toastTimer = null
function showToast(msg) {
  toast.value = msg
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (toast.value = ''), 3500)
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    overview.value = await getPlanOverview(props.parkId)
  } catch (err) {
    loadError.value = err.code === 'FORBIDDEN' ? 'Solo el administrador del parque puede ver esta sección.' : 'No se pudo cargar tu plan.'
  } finally {
    loading.value = false
  }
}

// Monto mensual que verá el administrador con el plan elegido (respeta el descuento vigente).
const newAmount = computed(() => {
  const price = modal.value?.plan?.monthlyPrice ?? 0
  const pct = overview.value?.plan?.discountPercent
  return pct ? Math.round(price * (1 - pct / 100) * 100) / 100 : price
})

async function confirmChange() {
  saving.value = true
  actionError.value = ''
  try {
    const plan = modal.value.plan
    await requestPlanChange(props.parkId, plan.id)
    modal.value = null
    await refresh()
    showToast(`Solicitud enviada: Plan ${plan.name}`)
  } catch (err) {
    actionError.value = errorText(err)
  } finally {
    saving.value = false
  }
}

async function onCancelRequest() {
  saving.value = true
  actionError.value = ''
  try {
    await requestPlanChange(props.parkId, null)
    await refresh()
    showToast('Solicitud cancelada')
  } catch (err) {
    actionError.value = errorText(err)
  } finally {
    saving.value = false
  }
}

// Recarga sin el estado de "cargando" para que la pantalla no parpadee.
async function refresh() {
  overview.value = await getPlanOverview(props.parkId)
}

async function onDownload(invoice) {
  downloading.value = true
  actionError.value = ''
  try {
    await downloadReceipt(props.parkId, invoice?.id ?? null, invoice ? `comprobante-${invoice.folio}.pdf` : 'comprobantes-parkos.pdf')
  } catch (err) {
    actionError.value = errorText(err, 'No se pudo descargar el comprobante. Intenta de nuevo.')
  } finally {
    downloading.value = false
  }
}

function onFiscalSaved(fiscal) {
  overview.value = { ...overview.value, fiscal }
  showToast('Datos de facturación guardados')
}

onMounted(load)
</script>

<style scoped>
.plan { display: flex; flex-direction: column; gap: 20px; max-width: 860px; margin: 0 auto; padding-bottom: 32px; }
.plan__state { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 48px 0; font: 400 13px var(--font-body); color: #64748b; }
.plan__state--error { color: #b91c1c; }
.plan__retry { background: none; border: none; color: #3b82f6; cursor: pointer; font: 500 13px var(--font-body); text-decoration: underline; }
.plan__spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.plan__ro { margin: 0; padding: 8px 12px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px; font: 400 12px var(--font-body); color: #64748b; }
.plan__error { margin: 0; font: 400 12px var(--font-body); color: #b91c1c; }

.banner { display: flex; align-items: center; gap: 10px; min-height: 56px; padding: 8px 16px; background: #eff6ff; border-bottom: 1px solid #bfdbfe; border-radius: 8px 8px 0 0; box-sizing: border-box; }
.banner__icon { font-size: 16px; color: #3b82f6; }
.banner__text { flex: 1; font: 400 12px/1.45 var(--font-body); color: #1d4ed8; }
.banner__btn { padding: 8px 14px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 12px var(--font-body); cursor: pointer; white-space: nowrap; }
.banner__btn:hover { background: #2563eb; }

.btn { height: 36px; padding: 0 16px; border: none; border-radius: 8px; background: #3b82f6; color: #fff; font: 500 13px var(--font-body); cursor: pointer; }
.btn:hover:not(:disabled) { background: #2563eb; }
.btn:disabled { opacity: 0.55; cursor: default; }
.btn--ghost { background: none; color: #64748b; }
.btn--ghost:hover:not(:disabled) { background: #f1f5f9; }
.sum { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 8px; }
.sum strong { color: #1e3a5f; }
.rcp { margin: 0; display: flex; flex-direction: column; }
.rcp > div { display: flex; justify-content: space-between; gap: 16px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
.rcp dt { color: #94a3b8; }
.rcp dd { margin: 0; color: #1e3a5f; text-align: right; }
.rcp__total dd { font-weight: 700; }
.rcp__note { margin: 12px 0 0; font-size: 11px; color: #94a3b8; }
.mono { font-family: var(--font-mono); }
.toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 10px 18px; border-radius: 8px; background: #1e3a5f; color: #fff; font: 400 13px var(--font-body); z-index: 1600; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.25); }
</style>